import {valueSchema, answerSchema} from './schema.js'
import {validate} from '../../../lib/schema-validator.js'

export const DATA_POINT_ID = 'which_of_the_following_recruitment_fees_or_related_costs_does_t'

export const dataPointDefinition = {
  retracedId: 'which_of_the_following_recruitment_fees_or_related_costs_does_t',
  name: 'Which of the following recruitment fees or related costs does the worker pay for?',
  description:
    'The intent of this question is to understand which recruitment fees and related costs the worker is paying for. \r\r\n\r\r\nThe terms ‘recruitment fees’ or ‘related costs’ refer to any fees or costs incurred in the recruitment process in order for workers to secure employment or placement, regardless of the manner, timing or location of their imposition or collection.\r\r\n\r\r\nWhen initiated by an employer; required to secure access to employment or placement; or imposed during the recruitment process, the following costs should be considered related to the recruitment process:\r\r\ni. Medical costs: payments for medical examinations, tests or vaccinations;\r\r\nii. Insurance costs: costs to insure the lives, health and safety of workers, including enrollment in migrant welfare funds;\r\r\niii. Costs for skills and qualification tests: costs to verify workers’ language proficiency and level of skills and qualifications, as well as for location-specific credentialing, certification or licensing;\r\r\niv. Costs for training and orientation: expenses for required trainings, including on-site job orientation and pre-departure or post-arrival orientation of newly recruited workers;\r\r\nv. Equipment costs: costs for tools, uniforms, safety gear, and other equipment needed to perform assigned work safely and effectively;\r\r\nvi. Travel and lodging costs: expenses incurred for travel, lodging and subsistence within or across national borders in the recruitment process, including for training, interviews, consular appointments, relocation, and return or repatriation;\r\r\nvii. Administrative costs: application and service fees that are required for the sole purpose of fulfilling the recruitment process. These could include fees for representation and services aimed at preparing, obtaining or legalizing workers’ employment contracts, identity documents, passports, visas, background checks, security and exit clearances, banking services, and work and residence permits. \r\r\nhttps://www.ilo.org/wcmsp5/groups/public/---ed_protect/---protrav/---migrant/documents/publication/wcms_536755.pdf',
  meta: {},
  fieldTypeId: 'CHECK_BOX',
  type: 'RELATIONAL',
  options: [
    {
      key: 'SKILLS_AND_QUALIFICATION_TESTS',
      text: 'Skills and qualification tests',
      externalIds: [
        {
          id: 'fl-4--2-4x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'MEDICAL_COSTS',
      text: 'Medical costs',
      externalIds: [
        {
          id: 'fl-4--2-5x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'TRAINING_AND_ORIENTATION',
      text: 'Training and orientation',
      externalIds: [
        {
          id: 'fl-4--2-6x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'ADMINISTRATIVE_COSTS',
      text: 'Administrative costs',
      externalIds: [
        {
          id: 'fl-4--2-7x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'TRAVEL_AND_LODGING',
      text: 'Travel and lodging',
      externalIds: [
        {
          id: 'fl-4--2-8x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'EQUIPMENT_COSTS',
      text: 'Equipment costs',
      externalIds: [
        {
          id: 'rh-rec-2x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'INSURANCE_COSTS',
      text: 'Insurance costs',
      externalIds: [
        {
          id: 'rh-rec-3x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
    {
      key: 'OTHER',
      text: 'Other',
      externalIds: [
        {
          id: 'fl-4--2-10x',
          version: '1.4.0',
          provider: 'slcp',
        },
      ],
    },
  ],
  schema: {
    type: 'object',
    properties: {
      value: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              enum: [
                'SKILLS_AND_QUALIFICATION_TESTS',
                'MEDICAL_COSTS',
                'TRAINING_AND_ORIENTATION',
                'ADMINISTRATIVE_COSTS',
                'TRAVEL_AND_LODGING',
                'EQUIPMENT_COSTS',
                'INSURANCE_COSTS',
                'OTHER',
              ],
            },
            isChecked: {
              type: 'boolean',
            },
            followUp: {
              type: ['object', 'null'],
              properties: {
                value: {
                  type: 'string',
                },
              },
              required: ['value'],
            },
          },
          required: ['key', 'isChecked', 'followUp'],
        },
      },
    },
    required: ['value'],
  },
  externalIds: [
    {
      id: 'rh-rec-1',
      version: '1.4.0',
      provider: 'slcp',
    },
  ],
  sortingId: 4910,
  sectionId: '2.2.4.1',
  impactArea: 'Recruitment, Hiring & Termination Practices',
  impactAreaId: 'RECRUITMENT_HIRING_AND_TERMINATION_PRACTICES',
}

export const validateSchema = data => {
  try {
    validate({value: data, schema: valueSchema})
    return data
  } catch (error) {
    throw new Error(`${DATA_POINT_ID}: ${error}`)
  }
}

export const transformAnswer = (answer, {meta}) => {
  try {
    validate({value: answer, schema: answerSchema})
    const formattedDataPointKey = DATA_POINT_ID.replace(/_([a-z])/g, g => g[1].toUpperCase())
    return {[formattedDataPointKey]: answer}
  } catch (error) {
    throw new Error(`${DATA_POINT_ID}: ${error}`)
  }
}
